import type {
  MapDefinition,
  PlayerIdentity,
  PublicRoom,
  RaceSnapshot,
  RacerSnapshot,
  VisualTuning,
} from './types.js';
import type { RaceWireFrame } from './snapshot-codec.js';
import type { LobbySettingsPatch } from './lobby.js';

export interface PlayerInput {
  throttle: number;
  brake: number;
  steer: number;
  handbrake: boolean;
  nitro: boolean;
  turret: number;
  fire: boolean;
  reset: boolean;
  /** Banks a completed lap in Heat Run. Ignored in every other event. */
  bank?: boolean;
  /** Rejects delayed controls that belonged to an earlier in-room restart. */
  raceAttempt?: number;
  sequence: number;
}

/**
 * The locally predicted motion uploaded once after a reconnect in non-competitive
 * rooms. Race progress, damage and consumables deliberately remain authoritative.
 */
export interface ResumeState {
  x: number;
  y: number;
  /** Last known road deck, kept separate from an airborne chassis height. */
  groundY: number;
  z: number;
  yaw: number;
  travelYaw: number;
  turretYaw: number;
  velocityX: number;
  velocityZ: number;
  verticalSpeed: number;
  steer: number;
  drift: number;
  engineRpm: number;
  gear: number;
  wheelSlip: number;
  longitudinalAcceleration: number;
  lateralAcceleration: number;
  chassisPitch: number;
  chassisRoll: number;
  pitchVelocity?: number;
  rollVelocity?: number;
  angularVelocity: number;
  /** Baked main-road hint; -1 means an alternate route or an unknown ribbon. */
  trackSegment: number;
  inputSequence: number;
}

export type ClientMessage =
  | { type: 'input'; input: PlayerInput }
  | { type: 'resume'; state: ResumeState }
  | { type: 'ready'; ready: boolean }
  | { type: 'start' }
  | { type: 'restart'; raceAttempt?: number }
  | { type: 'post-race-vote'; vote: import('./types.js').PostRaceVote }
  /** `rtt` is the sender's latest measured round trip, shared with the room. */
  | { type: 'ping'; sentAt: number; rtt?: number }
  /** Host-only lobby edit; the server answers by re-welcoming every member. */
  | { type: 'configure'; settings: LobbySettingsPatch }
  /** Swap car or livery while the room is still in its lobby. */
  | { type: 'loadout'; carId: string; tuning: VisualTuning; crewModule?: import('./types.js').CrewModule }
  /** Host-only removal of a human; `ban` also refuses that browser's return. */
  | { type: 'kick'; playerId: string; ban: boolean }
  /** Leaves the room for good instead of holding a reconnect slot. */
  | { type: 'leave' }
  /** Ends the result screen and reopens the same room's lobby. */
  | { type: 'return-to-lobby' };

/** First WebSocket payload. Bearer credentials deliberately stay out of URLs
 * so reverse proxies and access logs cannot capture reconnect passes. */
export type SocketAuthenticationMessage =
  | { type: 'authenticate'; roomId: string; playerId: string; token: string }
  /** An empty inviteToken requests public access; the server checks visibility. */
  | { type: 'spectate'; roomId: string; inviteToken: string };

export type ServerMessage =
  | {
      type: 'welcome';
      protocolVersion: number;
      room: PublicRoom;
      map: MapDefinition;
      playerId: string;
      isHost: boolean;
      racers: RacerSnapshot[];
    }
  | {
      type: 'spectator-welcome';
      protocolVersion: number;
      room: PublicRoom;
      map: MapDefinition;
      racers: RacerSnapshot[];
    }
  | { type: 'room-state'; room: PublicRoom }
  | RaceSnapshot
  | RaceWireFrame
  | { type: 'player-joined'; racer: RacerSnapshot }
  | { type: 'player-left'; playerId: string }
  | { type: 'host-changed'; hostPlayerId: string }
  // Targeted at the superseded connection when a newer socket with the same
  // reconnect pass takes over input — typically the same driver in a second tab.
  | { type: 'connection-replaced' }
  | {
      type: 'post-race-vote';
      votes: Partial<Record<import('./types.js').PostRaceVote, number>>;
      required: number;
      resolved: import('./types.js').PostRaceVote | null;
    }
  | {
      type: 'party-transfer';
      room: PublicRoom;
      inviteToken: string;
    }
  | { type: 'pong'; sentAt: number; serverTime: number }
  /** Every connected driver's reported round trip in ms, every ~2 s. */
  | { type: 'latency'; pings: Record<string, number> }
  | { type: 'error'; code: string; message: string };

export interface CreateRoomRequest {
  name: string;
  /** Random browser-local identifier, hashed server-side for host bans. */
  deviceId?: string;
  /** Per-slot rival difficulty; overrides npcCount/npcDifficulty when present. */
  npcRoster?: import('./types.js').NpcDifficulty[];
  visibility?: import('./types.js').RoomVisibility;
  laps?: number;
  /** Human driver slots, 2–6. */
  maxPlayers?: number;
  mapId: string;
  npcCount: number;
  npcDifficulty: 'rookie' | 'sport' | 'pro';
  antiCheat: boolean;
  collisions: boolean;
  traffic?: boolean;
  timeOfDay?: MapDefinition['timeOfDay'];
  weather?: MapDefinition['weather'];
  timeTrial?: boolean;
  eventMode?: import('./types.js').RaceEventMode;
  arcadePreset?: import('./types.js').ArcadePreset;
  crewModule?: import('./types.js').CrewModule;
  tourId?: string;
  tourStage?: number;
  featuredRival?: string;
  wager?: import('./types.js').RaceWagerKind;
  dailyChallengeId?: string;
  signatureActive?: boolean;
  carId: string;
  tuning: VisualTuning;
}

export interface CreateRoomResponse {
  room: PublicRoom;
  player: PlayerIdentity;
  inviteToken: string;
}

export interface JoinRoomRequest {
  /** Required for private rooms; public lobbies can be joined from the browser. */
  inviteToken: string;
  /** Random browser-local identifier, hashed server-side for host bans. */
  deviceId?: string;
  name: string;
  carId: string;
  tuning: VisualTuning;
  crewModule?: import('./types.js').CrewModule;
  signatureActive?: boolean;
}

export interface JoinRoomResponse {
  room: PublicRoom;
  player: PlayerIdentity;
  /** Returned so a member who arrived via the public browser can invite friends. */
  inviteToken?: string;
}

/** Close codes that end a session for good; the client must not reconnect. */
export const SOCKET_CLOSE_SESSION_ENDED = 4001;
export const SOCKET_CLOSE_UNAUTHORISED = 4003;
export const SOCKET_CLOSE_KICKED = 4004;
export const SOCKET_CLOSE_BANNED = 4005;
export const TERMINAL_SOCKET_CLOSE_CODES: ReadonlySet<number> = new Set([
  SOCKET_CLOSE_SESSION_ENDED,
  SOCKET_CLOSE_UNAUTHORISED,
  SOCKET_CLOSE_KICKED,
  SOCKET_CLOSE_BANNED,
]);

export interface CreateSuccessorRequest {
  playerId: string;
  playerToken: string;
  vote: Exclude<import('./types.js').PostRaceVote, 'rematch'>;
}

export interface CreateSuccessorResponse {
  room: PublicRoom;
  inviteToken: string;
}
